import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {debounceTime, distinctUntilChanged, skipWhile, takeUntil, tap} from 'rxjs/operators';
import {EquipmentInterfaces} from '@namespaces/equipment.namespace';
import {FormControl} from '@angular/forms';
import {Subscription} from 'rxjs';
import {Subject} from 'rxjs';
import {EquipmentSelectControlService} from './services/equipment-select-control.service';
import {EquipmentSelect} from './equipment-select.namespace';
import ViewStatus = EquipmentSelect.ViewStatus;
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';

@Component({
    selector: 'app-equipment-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './equipment-select.component.html',
    styles: [`
        [data-equipment-item] {
            padding: 15px 10px;
            margin: 0 -10px;
            min-height: 52px;
        }
        [data-equipment-item]:hover {
            cursor: pointer;
            background: #f7f7f7;
            transition: background 0.25s;
        }
    `],
    providers: [
    ]
})
export class EquipmentSelectComponent implements OnInit, OnDestroy {
    @Input() selectedEquipmentControl = new FormControl('');

    /**
     * Выбранная модель оборудования
     */
    @Input() selectedEquipment: EquipmentInterfaces.Model;

    @Input() allowEmptyTitle = true;

    @Output() selectItem = new EventEmitter<EquipmentInterfaces.Model>();

    public defaultEquipmentId: number;
    public defaultSelectedEquipment: EquipmentInterfaces.Model;

    public equipment: EquipmentInterfaces.Model[] = [];

    public titleSearchString = new FormControl('');

    public isLoading = false;
    public isSearched = false;

    public page = 1;
    public pagesTotal = 1;
    public countFound = null;
    public countTotal = null;

    public viewStatusesList = ViewStatus;

    public viewStatus = ViewStatus.search;

    private subs: Subscription[] = [];

    private updateView$ = new Subject();

    /**
     * Прерывание текущего запроса поиска по событию
     */
    private preventSearch$ = new Subject();

    constructor(
        private cd: ChangeDetectorRef,
        public equipmentSelectControl: EquipmentSelectControlService,
        private authService: AuthService,
        @Inject('EquipmentSearchService') private searchService,
    ) {
    }

    ngOnInit() {
        const initialTitleValue = this.titleSearchString.value;

        /**
         * В этом условии по сути инициализация дефолтных параметров
         */
        if (this.selectedEquipmentControl.value) {
            this.defaultEquipmentId = this.selectedEquipmentControl.value;
            this.equipmentSelectControl.setSelectedEquipmentId(this.defaultEquipmentId);

            if (this.selectedEquipment) {
                this.equipmentSelectControl.setSelectedEquipment(this.selectedEquipment);
                this.defaultSelectedEquipment = this.selectedEquipment;
            }

            this.equipmentSelectControl.setViewStatus(ViewStatus.preview);
        }

        this.subs.push(
            this.titleSearchString.valueChanges.pipe(
                distinctUntilChanged(),
                /**
                 * Таким костылем избавляемся от ситуации, когда кликнули в поле и из него,
                 * значение по факту поменялось
                 */
                skipWhile(value => value === initialTitleValue),
                tap(() => {
                    this.isLoading = true;
                    this.updateView$.next();
                }),
                debounceTime(300),
            ).subscribe((title: string) => {
                this.page = 1;
                this.pagesTotal = 1;
                this.equipment = [];

                this.isSearched = Boolean(title.length);

                if (title.length || this.allowEmptyTitle) {
                    this.updateItems();
                } else {
                    this.countFound = null;
                    this.countTotal = null;
                    this.isLoading = false;
                    this.updateView$.next();
                }
            }),
            this.equipmentSelectControl.viewStatus$.subscribe((status: ViewStatus) => {
                this.viewStatus = status;

                if (this.viewStatus === ViewStatus.search) {
                    this.equipmentSelectControl.setSelectedEquipment(null);
                    this.equipmentSelectControl.setSelectedEquipmentId(null);

                    if (this.allowEmptyTitle && !this.selectedEquipmentControl.value) {
                        this.updateItems();
                    }
                }

                this.updateView$.next();
            }),
            this.equipmentSelectControl.selectedEquipmentId$.subscribe((id: number) => {
                this.selectedEquipmentControl.patchValue(id);
                this.updateView$.next();
            }),
            this.equipmentSelectControl.selectedEquipment$.subscribe((model: EquipmentInterfaces.Model) => {
                this.selectItem.emit(model);
                this.selectedEquipment = model;
                this.updateView$.next();
            }),
            this.updateView$.pipe(
                debounceTime(10)
            ).subscribe(() => this.cd.detectChanges())
        );

        /**
         * Если разрешен поиск с пустым полем наименования и нет выбранного оборудования, то инициализируем поиск
         */
        if (this.allowEmptyTitle && !this.selectedEquipmentControl.value) {
            this.updateItems();
        }

        this.equipmentSelectControl.setCanCreateOwn(this.authService.hasPermissions([UserNamespace.Permission.createOwnEquipment]));
    }

    private updateItems() {
        this.isLoading = true;
        /**
         * Предотвращаем все текущие запросы поиска
         */
        this.preventSearch$.next();
        this.updateView$.next();

        const title: string = this.titleSearchString.value;

        this.searchService.getEquipment(title.trim(), this.page).pipe(
            takeUntil(this.preventSearch$)
        ).toPromise().then((list: EquipmentListResponse) => {
            /**
             * Если запрос был прерван, то list будет undefined
             */
            if (list) {
                this.equipment = list.items;
                this.page = list.page;
                this.pagesTotal = list.pages_total;
                this.countFound = list.total_found_count;
                this.countTotal = list.total_items_count;
                this.isLoading = false;
            } else {
                this.equipment = [];
                this.page = 1;
                this.pagesTotal = 1;
                this.countFound = null;
                this.countTotal = null;
            }
            this.updateView$.next();
        }, (error) => {
            this.equipment = [];
            this.page = 1;
            this.pagesTotal = 1;
            this.countFound = null;
            this.countTotal = null;
            this.isLoading = false;
            this.updateView$.next();
        });
    }

    setDefault() {
        this.equipmentSelectControl.setSelectedEquipment(this.defaultSelectedEquipment);
        this.equipmentSelectControl.setViewStatus(ViewStatus.preview);
        this.equipmentSelectControl.setSelectedEquipmentId(this.defaultEquipmentId);
    }

    onItemSelect(item: EquipmentInterfaces.Model) {
        this.equipmentSelectControl.setSelectedEquipment(item);
        this.equipmentSelectControl.setSelectedEquipmentId(+item.id);
        this.equipmentSelectControl.setViewStatus(ViewStatus.preview);
    }

    onPageChange(page: number) {
        this.page = page;
        this.updateItems();
    }

    onAddOwnClick() {
        this.equipmentSelectControl.setViewStatus(ViewStatus.creationForm);
    }

    onFullViewClick(item: EquipmentInterfaces.Model) {
        this.selectedEquipment = item;
        this.equipmentSelectControl.setViewStatus(ViewStatus.fullView);
    }

    showSearch() {
        this.equipmentSelectControl.setViewStatus(ViewStatus.search);
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.equipmentSelectControl.reset();
    }
}

export interface EquipmentListResponse {
    items: EquipmentInterfaces.Model[];
    total_items_count: number;   // Всего единиц оборудования (например 1290)
    total_found_count: number;   // Количество найденного оборудования по фильтру (например 166)
    page: number;                // Номер текущей страницы
    pages_total: number;         // Всего страниц
}
